import { TApiOrder, TApiOrderDetails } from '../../api/endpoints/Order';
import {
  IApproveOrderCompletedAction,
  ICancelOrderCompletedAction,
  ICreateOrderCompletedAction,
  IFetchOrdersCompletedAction,
  IFetchOrdersFailedAction,
  IFetchOrdersStartedAction,
  IMoveOrderRoFolderCompletedAction,
  IRejectOrderCompletedAction,
  IRemoveOrderCompletedAction,
  ISubmitOrderCompletedAction,
  IUpdateOrderCompletedAction,
  ORDER_APPROVE_COMPLETED,
  ORDER_CANCEL_COMPLETED,
  ORDER_CREATE_COMPLETED,
  ORDER_REJECT_COMPLETED,
  ORDER_REMOVE_COMPLETED,
  ORDER_SUBMIT_COMPLETED,
  ORDER_UPDATE_COMPLETED,
  ORDERS_FETCH_COMPLETED,
  ORDERS_FETCH_FAILED,
  ORDERS_FETCH_STARTED,
  ORDERS_MOVE_COMPLETED
} from '../action-creators/orders';
import { orderBy } from 'lodash';
import { Nullable } from '../../../types/utils';

export type TOrdersState = Record<number, TOrdersSubState | undefined>;

export type TOrdersSubState = {
  loading: boolean;
  loadedAt: null | number;
  error: null | Error;
  data: Array<TApiOrder>
};

const initialState: TOrdersState = {
};

const initialSubState = () => ({
  loading: false,
  loadedAt: null,
  error: null,
  data: []
})


type TAllowedActions =
  ICreateOrderCompletedAction |
  IUpdateOrderCompletedAction |
  IFetchOrdersStartedAction |
  IFetchOrdersCompletedAction |
  IFetchOrdersFailedAction |
  ISubmitOrderCompletedAction |
  ICancelOrderCompletedAction |
  IApproveOrderCompletedAction |
  IRejectOrderCompletedAction |
  IMoveOrderRoFolderCompletedAction |
  IRemoveOrderCompletedAction


const parseOrder = (order: TApiOrder | TApiOrderDetails): TApiOrder => {
  return {
    id: order.id,
    ccp_organisation: order.ccp_organisation,
    organisation_id: order.organisation_id,
    category: order.category,
    status: order.status,
    customer: order.customer,
    name: order.name,
    description: order.description,
    has_legs: order.has_legs,
    created: order.created,
    updated: order.updated
  };
}

const sortOrders = (orders: Array<TApiOrder>): Array<TApiOrder> => {
  return orderBy(
    orders,
    [order => new Date(order.updated).getTime()],
    ['desc']
  );
}

const addOrder = (data: TOrdersSubState['data'], order: TApiOrder | TApiOrderDetails): Array<TApiOrder> => {
  return sortOrders([...data, parseOrder(order)]);
}

const updateOrder = (data: TOrdersSubState['data'], order: TApiOrder | TApiOrderDetails) => {
  return data.map(item => {
    if (item.id === order.id) {
      return parseOrder(order);
    }

    return item;
  });
};

const removeOrder = (data: TOrdersSubState['data'], orderId: number) => {
  return data.filter(item => item.id !== orderId);
}

const moveOrder = (
  state: TOrdersState,
  order: TApiOrder,
  srcFolderId: Nullable<number>,
  targetFolderId: Nullable<number>
) => {
  let newState = updateSubState(state, srcFolderId, subState => ({
    ...subState,
    data: removeOrder(subState.data, order.id)
  }));

  newState = updateSubState(newState, targetFolderId, subState => ({
    ...subState,
    data: addOrder(subState.data, order)
  }))

  return newState;
}

const updateSubState = (state: TOrdersState, folderId: Nullable<number> | undefined, callback: (subState: TOrdersSubState) => TOrdersSubState) => {
  const key = folderId ? folderId : -1;
  const currentSubState = state[key] || initialSubState();

  const updatedSubState = callback(currentSubState);

  return {
    ...state,
    [key]: updatedSubState
  };
}

const ordersReducer = (state: TOrdersState = initialState, action: TAllowedActions): TOrdersState => {
  switch (action.type) {
    case ORDERS_FETCH_STARTED:
      return updateSubState(state, action.folderId, (subState) => ({
        ...subState,
        loading: true,
        error: null
      }));

    case ORDERS_FETCH_COMPLETED:
      return updateSubState(state, action.folderId, (subState) => ({
        ...subState,
        loadedAt: Date.now(),
        loading: false,
        error: null,
        data: action.data
      }));

    case ORDERS_FETCH_FAILED:
      return updateSubState(state, action.folderId, (subState) => ({
        ...subState,
        loading: false,
        error: action.error
      }));

    case ORDER_CREATE_COMPLETED:
      return updateSubState(state, action.folderId, (subState) => ({
        ...subState,
        data: addOrder(subState.data, action.order)
      }));

    case ORDER_UPDATE_COMPLETED:
      return updateSubState(state, action.folderId, (subState) => ({
        ...subState,
        data: updateOrder(subState.data, action.order)
      }));

    case ORDER_SUBMIT_COMPLETED:
      return updateSubState(state, action.folderId, (subState) => ({
        ...subState,
        data: updateOrder(subState.data, action.order)
      }));

    case ORDER_CANCEL_COMPLETED:
      return updateSubState(state, action.folderId, (subState) => ({
        ...subState,
        data: updateOrder(subState.data, action.order)
      }));

    case ORDER_APPROVE_COMPLETED:
      return updateSubState(state, action.folderId, (subState) => ({
        ...subState,
        data: updateOrder(subState.data, action.order)
      }));

    case ORDER_REJECT_COMPLETED:
      return updateSubState(state, action.folderId, (subState) => ({
        ...subState,
        data: updateOrder(subState.data, action.order)
      }));

    case ORDERS_MOVE_COMPLETED:
      return moveOrder(state, action.order, action.srcFolderId, action.targetFolderId);

    case ORDER_REMOVE_COMPLETED:
      return updateSubState(state, action.folderId, (subState) => ({
        ...subState,
        data: removeOrder(
          subState.data,
          typeof action.orderId === 'number' ? action.orderId : parseInt(action.orderId, 10))
      }))

    default:
      return state;
  }
};

export default ordersReducer;
